import React, { createContext, useContext } from 'react'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import { useFinancialWorkspace } from 'modules/finance/components/FinancialWorkspace/Providers/Provider'
import { usePanel } from 'modules/finance/components/FinancialWorkspace/components/ResponsivePageWithPanel/provider'
import { useFinancialWorkspaceAvailabilities } from 'modules/finance/components/FinancialWorkspace/hooks/useFinancialWorkspaceAvailabilities'
import { HistoryPanel } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/components/HistoryPanel'
import { useAgreementsData } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/hooks/useAgreementsData'
import { useExpensesData } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/hooks/useExpensesData'
import { usePayrollData } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/hooks/usePayrollData'
import { HookData } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/types/data'

interface Props {
  children: React.ReactNode
}

interface IValue {
  loading: boolean
  payrollData: HookData
  expensesData: HookData
  agreementData: HookData
}

const DataProviderContext = createContext<IValue | undefined>(undefined)

export const DataProvider: React.FC<Props> = ({ children }) => {
  const { formattedState } = useFinancialWorkspace()
  const {
    features: { dev_full_time_equivalent },
  } = useFinancialWorkspaceAvailabilities()

  const { setContent } = usePanel()

  const agreementData: HookData = useAgreementsData()
  const expensesData: HookData = useExpensesData()
  const payrollData: HookData = usePayrollData()

  useUnsafeEffect(() => {
    setContent(
      <HistoryPanel formattedState={formattedState} fteEnabled={dev_full_time_equivalent} />
    )
  }, [setContent, formattedState, dev_full_time_equivalent])

  return (
    <DataProviderContext.Provider
      value={{
        payrollData,
        expensesData,
        agreementData,
        loading: !!(expensesData?.loading || payrollData?.loading || agreementData?.loading),
      }}
    >
      {children}
    </DataProviderContext.Provider>
  )
}

export const useData = () => {
  const context = useContext(DataProviderContext)
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider')
  }
  return context
}
